import { Menu, useMenu } from "@/components/collection"
import { usePersistedState } from "@/components/cookies/hooks/usePersistedState"
import { StatsDialog } from "@/components/trackings/dialogs/stats"
import { Confirm } from "@/components/ui/confirm"
import { useConfirm } from "@/components/ui/hooks/useConfirm"
import { useDialog } from "@/components/ui/hooks/useDialog"
import globalConfig from "@/config/global"
import { useSelectItem } from "@/hooks/useSelect"
import { Place } from "@/store/places/localizers"
import { deleteWorkshop, updateWorkshop } from "@/store/workshops/actions"
import { useWorkshopsById } from "@/store/workshops/hooks"
import { Workshop } from "@/store/workshops/localizers"
import { SelectionPlus, SelectionSlash } from "@phosphor-icons/react"
import { Archive, Eye, EyeOff, Pen, PlusSquare, Rocket, Trash } from "lucide-react"
import { CreateDialog } from "./CreateDialog"
import { EditDialog } from "./EditDialog"

/**
 * dictionary src/dictionaries/en/pages/dashboard/kultureralley/workshops.json
 */
const dictionary = createContextMapper("pages", "dashboard", "kultureralley", "workshops")

/**
 * Context workshops
 */
export const Context = React.createContext({} as ContextType)
export const usePageContext = () => React.useContext(Context)
export const ContextProvider: React.FC<React.PropsWithChildren<{ place: Place }>> = ({
  place,
  children,
}) => {
  const workshopsById = useWorkshopsById()

  // selection
  const [selected, setSelected] = React.useState<string[]>([])

  // view
  const [view, setView] = usePersistedState<ContextType["view"]>(
    "list",
    "state-workshops-view",
    "interface",
    sessionStorage
  )

  // actions
  const publish = (id: string) => updateWorkshop(id, { published: true })
  const unpublish = (id: string) => updateWorkshop(id, { published: false })
  const archive = (id: string) => updateWorkshop(id, { archived: true })

  // confirms
  const displayName = (id: string) => D.get(workshopsById, id)?.name ?? ""
  const { confirm: confirmDelete, ...deleteProps } = useConfirm<string>({
    onAsyncConfirm: deleteWorkshop,
    dictionary: Confirm.dictionary(dictionary("delete-confirm")),
  })
  const { confirm: confirmDeleteSelection, ...deleteSelectionProps } = useConfirm<void, string>({
    list: selected,
    displayName,
    onAsyncConfirm: deleteWorkshop,
    dictionary: Confirm.dictionary(dictionary("delete-selection-confirm")),
  })
  const { confirm: confirmPublishSelection, ...publishSelectionProps } = useConfirm<void, string>({
    list: selected,
    displayName,
    onAsyncConfirm: id => updateWorkshop(id, { published: true }),
    dictionary: Confirm.dictionary(dictionary("publish-selection-confirm")),
  })
  const { confirm: confirmUnpublishSelection, ...unpublishSelectionProps } = useConfirm<
    void,
    string
  >({
    list: selected,
    displayName,
    onAsyncConfirm: id => updateWorkshop(id, { published: false }),
    dictionary: Confirm.dictionary(dictionary("unpublish-selection-confirm")),
  })
  const { confirm: confirmArchive, ...archiveProps } = useConfirm<string>({
    onAsyncConfirm: archive,
    dictionary: Confirm.dictionary(dictionary("archive-confirm")),
  })
  const { confirm: confirmArchiveSelection, ...archiveSelectionProps } = useConfirm<void, string>({
    list: selected,
    displayName,
    onAsyncConfirm: archive,
    dictionary: Confirm.dictionary(dictionary("archive-selection-confirm")),
  })

  // dialogs
  const { setItem: create, ...createProps } = useDialog<void>()
  const { setItem: edit, ...editProps } = useDialog<Workshop>()
  const { setItem: openStats, ...statsProps } = useDialog<string>()

  return (
    <Context.Provider
      value={{
        place,
        selected,
        setSelected,
        view,
        setView,
        create,
        edit,
        publish,
        unpublish,
        confirmDelete,
        confirmDeleteSelection,
        confirmPublishSelection,
        confirmUnpublishSelection,
        confirmArchive,
        confirmArchiveSelection,
        openStats,
      }}
    >
      {children}
      <Confirm {...deleteProps} />
      <Confirm {...deleteSelectionProps} />
      <Confirm {...publishSelectionProps} />
      <Confirm {...unpublishSelectionProps} />
      <Confirm {...archiveProps} />
      <Confirm {...archiveSelectionProps} />
      <CreateDialog {...createProps} />
      <EditDialog {...editProps} />
      <StatsDialog {...statsProps} />
    </Context.Provider>
  )
}

/**
 * ContextMenu
 */
export const ContextMenu: React.FC<{
  workshop: Workshop
}> = ({ workshop }) => {
  const { _ } = useDictionary(dictionary("menu"))
  const {
    confirmDelete,
    edit,
    create,
    publish,
    unpublish,
    confirmDeleteSelection,
    confirmPublishSelection,
    confirmUnpublishSelection,
    confirmArchive,
    confirmArchiveSelection,
  } = usePageContext()
  const { id } = workshop
  const { isSelected, toggleSelection } = useSelectItem(Context, workshop.id)
  const { type } = useMenu()
  const isContextMenu = type === "context-menu"
  return (
    <>
      <Menu.Item onClick={toggleSelection}>
        {isSelected ? <SelectionSlash aria-hidden /> : <SelectionPlus aria-hidden />}
        {_(isSelected ? "unselect" : "select")}
      </Menu.Item>
      <Menu.Item onClick={() => navigate(`/dashboard/kultureralley/${workshop.placeId}/${id}`)}>
        <Rocket aria-hidden />
        {_("display")}
      </Menu.Item>
      <Menu.Item onClick={() => edit(workshop)}>
        <Pen aria-hidden />
        {_("edit")}
      </Menu.Item>
      {workshop.published ? (
        <Menu.Item onClick={() => unpublish(id)}>
          <EyeOff aria-hidden />
          {_("unpublish")}
        </Menu.Item>
      ) : (
        <Menu.Item onClick={() => publish(id)}>
          <Eye aria-hidden />
          {_("publish")}
        </Menu.Item>
      )}
      {!globalConfig.disableKulturrallyeArchive && !workshop.archived && (
        <Menu.Item onClick={() => confirmArchive(id)}>
          <Archive aria-hidden />
          {_("archive")}
        </Menu.Item>
      )}
      <Menu.Item onClick={() => confirmDelete(id)}>
        <Trash aria-hidden />
        {_("delete")}
      </Menu.Item>
      {isContextMenu && (
        <>
          <Menu.Separator />
          <Menu.Item onClick={create}>
            <PlusSquare aria-hidden />
            {_("create")}
          </Menu.Item>
          {isSelected && (
            <>
              <Menu.Separator />
              {workshop.published ? (
                <Menu.Item onClick={confirmUnpublishSelection}>
                  <EyeOff aria-hidden />
                  {_("unpublish-selection")}
                </Menu.Item>
              ) : (
                <Menu.Item onClick={confirmPublishSelection}>
                  <Eye aria-hidden />
                  {_("publish-selection")}
                </Menu.Item>
              )}
              {!globalConfig.disableKulturrallyeArchive && !workshop.archived && (
                <Menu.Item onClick={confirmArchiveSelection}>
                  <Archive aria-hidden />
                  {_("archive-selection")}
                </Menu.Item>
              )}
              <Menu.Item onClick={confirmDeleteSelection}>
                <Trash aria-hidden />
                {_("delete-selection")}
              </Menu.Item>
            </>
          )}
        </>
      )}
    </>
  )
}

/**
 * types
 */
type ContextType = {
  place: Place
  selected: string[]
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
  view: "grid" | "list"
  setView: React.Dispatch<ContextType["view"]>
  confirmDelete: (value: string) => void
  edit: (value: Workshop) => void
  publish: (id: string) => void
  unpublish: (id: string) => void
  create: () => void
  confirmDeleteSelection: () => void
  confirmPublishSelection: () => void
  confirmUnpublishSelection: () => void
  confirmArchive: (value: string) => void
  confirmArchiveSelection: () => void
  openStats: (trackingId: string) => void
}
